import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { VolumeHealthStatus, VolumeHealth } from '@/service/gpu-instance/volume-type';
import { cn } from '@/lib/utils';
import { CheckCircle2, AlertTriangle, AlertCircle, XCircle } from 'lucide-react';

interface VolumeHealthIndicatorProps {
  health: VolumeHealth;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const healthConfig: Record<
  VolumeHealthStatus,
  {
    label: string;
    icon: typeof CheckCircle2;
    className: string;
    iconClassName: string;
  }
> = {
  [VolumeHealthStatus.Healthy]: {
    label: 'Healthy',
    icon: CheckCircle2,
    className: 'text-green-600 bg-green-50 dark:bg-green-950/30 border-green-200',
    iconClassName: 'text-green-500',
  },
  [VolumeHealthStatus.Warning]: {
    label: 'Warning',
    icon: AlertTriangle,
    className: 'text-yellow-700 bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200',
    iconClassName: 'text-yellow-500',
  },
  [VolumeHealthStatus.Degraded]: {
    label: 'Degraded',
    icon: AlertCircle,
    className: 'text-orange-700 bg-orange-50 dark:bg-orange-950/30 border-orange-200',
    iconClassName: 'text-orange-500',
  },
  [VolumeHealthStatus.Error]: {
    label: 'Error',
    icon: XCircle,
    className: 'text-red-700 bg-red-50 dark:bg-red-950/30 border-red-200',
    iconClassName: 'text-red-500',
  },
};

const sizeConfig = {
  sm: { icon: 'h-3 w-3', badge: 'text-xs px-1.5 py-0' },
  md: { icon: 'h-4 w-4', badge: 'text-xs px-2 py-0.5' },
  lg: { icon: 'h-5 w-5', badge: 'text-sm px-2.5 py-1' },
};

export const VolumeHealthIndicator = ({
  health,
  showLabel = true,
  size = 'md',
  className,
}: VolumeHealthIndicatorProps) => {
  const config = healthConfig[health.status];
  const sizes = sizeConfig[size];
  const Icon = config.icon;

  const content = (
    <Badge
      variant="outline"
      className={cn('gap-1 font-medium', config.className, sizes.badge, className)}
    >
      <Icon className={cn(sizes.icon, config.iconClassName)} />
      {showLabel && config.label}
    </Badge>
  );

  if (health.message || (health.alerts && health.alerts.length > 0)) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              {health.message && <p className="text-sm">{health.message}</p>}
              {health.alerts && health.alerts.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  {health.alerts.length} active alert{health.alerts.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};

export default VolumeHealthIndicator;
